import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Skeleton,
  LinearProgress,
  Tooltip,
  alpha,
  Tabs,
  Tab,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Activity, Clock, CalendarDays, AlertCircle, Plus } from 'lucide-react';
import dayjs from 'dayjs';

import { taskService } from '@/services/api';
import { QUERY_KEYS, queryClient } from '@/services/queryClient';
import { Task, TaskPriority, TaskStatus } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';
import StatusBadge from '@/shared/components/StatusBadge';
import { formatPercent } from '@/shared/utils/formatPercentage';

/**
 * LiveTasksWidget
 * Shows tasks where today falls between startDate and dueDate (Live tab)
 * and tasks that are past their due date (Overdue tab).
 * Displays a progress bar showing how far along the time window each task is.
 */
const LiveTasksWidget: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'live' | 'overdue'>('live');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; taskId: string | null; taskTitle: string | null }>({ 
    open: false, 
    taskId: null, 
    taskTitle: null 
  });

  const [pickError, setPickError] = useState('');

  // Query for today's picked tasks (to disable already-picked tasks)
  const { data: todaysPicksData } = useQuery({
    queryKey: QUERY_KEYS.todaysTasks,
    queryFn: () => taskService.getTodaysPicks(),
    staleTime: 30000,
  });

  const todaysPickedTaskIds = (todaysPicksData?.tasks ?? []).map((task: Task) => task.id);
  const todaysTodoPicks = (todaysPicksData?.tasks ?? []).filter(
    (task: Task) => task.status !== TaskStatus.COMPLETED && task.status !== TaskStatus.CANCELLED
  );
  const pickedCount = todaysPicksData?.pickedCount ?? todaysTodoPicks.length;
  const carriedForwardCount = todaysPicksData?.carriedForwardCount ?? 0;
  const maxDailyPicks = todaysPicksData?.maxDailyPicks ?? 5;
  const canPickMore = todaysPicksData?.canPickMore ?? pickedCount < maxDailyPicks;
  const atDailyPickLimit = !canPickMore;

  // Mutation for adding task to today
  const pickMutation = useMutation({
    mutationFn: (taskId: string) => taskService.pickTaskForToday(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.todaysTasks });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.liveTasks });
      setPickError('');
    },
    onError: (error: Error) => {
      setPickError(error.message || 'Failed to pick task for today');
    },
  });

  const { data: liveTasksData, isLoading, error } = useQuery({
    queryKey: QUERY_KEYS.liveTasks,
    queryFn: () => taskService.getLiveTasks(),
    staleTime: 60000,
  });

  const mergedTaskMap = new Map<string, Task>();
  const liveApiTasks = Array.isArray(liveTasksData) ? liveTasksData : [];
  liveApiTasks.forEach((task: Task) => mergedTaskMap.set(task.id, task));
  todaysTodoPicks.forEach((task: Task) => {
    if (!mergedTaskMap.has(task.id)) {
      mergedTaskMap.set(task.id, task);
    }
  });
  const tasks = Array.from(mergedTaskMap.values());

  // Separate tasks into live and overdue
  const now = dayjs();
  const liveTasks = tasks.filter((task: Task) => {
    if (!task.dueDate) return true;
    const dueDate = dayjs(task.dueDate);
    if (!dueDate.isValid()) return true;
    return dueDate.isAfter(now, 'day') || dueDate.isSame(now, 'day');
  });

  const overdueTasks = tasks.filter((task: Task) => {
    if (!task.dueDate) return false;
    const dueDate = dayjs(task.dueDate);
    if (!dueDate.isValid()) return false;
    return dueDate.isBefore(now, 'day');
  });

  // Determine which tasks to show based on active tab
  const displayTasks = activeTab === 'live' ? liveTasks : overdueTasks;

  // Sort: picked tasks first (focus today's commitments), then in_progress, then by deadline
  const sortedTasks = [...displayTasks].sort((a: Task, b: Task) => {
    const aIsPicked = todaysPickedTaskIds.includes(a.id);
    const bIsPicked = todaysPickedTaskIds.includes(b.id);
    if (aIsPicked && !bIsPicked) return -1;
    if (!aIsPicked && bIsPicked) return 1;

    // In progress first
    if (a.status === TaskStatus.IN_PROGRESS && b.status !== TaskStatus.IN_PROGRESS) return -1;
    if (b.status === TaskStatus.IN_PROGRESS && a.status !== TaskStatus.IN_PROGRESS) return 1;

    // Then by due date (soonest first)
    const aDue = dayjs(a.dueDate);
    const bDue = dayjs(b.dueDate);
    if (!aDue.isSame(bDue, 'day')) return aDue.diff(bDue);

    // Then by priority
    const priorityOrder: Record<string, number> = { p1: 0, p2: 1, p3: 2, p4: 3 };
    return (priorityOrder[a.priority] ?? 3) - (priorityOrder[b.priority] ?? 3);
  });

  /**
   * Calculate how far through the start→due window we are today.
   * Returns a percentage 0-100.
   */
  const getTimeProgress = (task: Task): number => {
    if (!task.startDate) return 0;
    const start = dayjs(task.startDate).startOf('day');
    const due = dayjs(task.dueDate).endOf('day');
    const now = dayjs();
    const totalMs = due.diff(start);
    if (totalMs <= 0) return 100;
    const elapsedMs = now.diff(start);
    return Math.min(100, Math.max(0, Math.round((elapsedMs / totalMs) * 100)));
  };

  /**
   * Get remaining days until the due date.
   */
  const getDaysRemaining = (dueDate: string): number => {
    return dayjs(dueDate).startOf('day').diff(dayjs().startOf('day'), 'day');
  };

  /**
   * Get color based on progress (green → amber → red as deadline nears).
   */
  const getProgressColor = (progress: number): 'success' | 'warning' | 'error' | 'primary' => {
    if (progress < 50) return 'success';
    if (progress < 75) return 'primary';
    if (progress < 90) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width={160} height={28} />
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ mt: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2, minHeight: 40 }}
        >
          <Tab
            value="live"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Activity size={16} />
                <span>Live</span>
                {liveTasks.length > 0 && (
                  <Chip
                    label={liveTasks.length}
                    size="small"
                    color="success"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.875rem' }}
          />
          <Tab
            value="overdue"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <AlertCircle size={16} />
                <span>Overdue</span>
                {overdueTasks.length > 0 && (
                  <Chip
                    label={overdueTasks.length}
                    size="small"
                    color="error"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>
            }
            sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.875rem' }}
          />
        </Tabs>

        {/* Tasks List - Scrollable container with max height */}
        {sortedTasks.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              borderRadius: 2,
            }}
          >
            {activeTab === 'live' ? (
              <>
                <Activity size={32} style={{ color: '#94a3b8', marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                  No live tasks right now
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tasks appear here when today is within their start–due window
                </Typography>
              </>
            ) : (
              <>
                <AlertCircle size={32} style={{ color: '#94a3b8', marginBottom: 8 }} />
                <Typography variant="body2" color="text.secondary">
                  No overdue tasks
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Great job! All tasks are on track
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              maxHeight: '400px',
              overflowY: 'auto',
              overflowX: 'hidden',
              pr: 0.5,
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                bgcolor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                bgcolor: 'divider',
                borderRadius: '3px',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              },
            }}
          >
            <List disablePadding sx={{ '& > *:not(:last-child)': { mb: 1 } }}>
              {sortedTasks.map((task: Task) => {
              const progress = getTimeProgress(task);
              const daysLeft = getDaysRemaining(task.dueDate);
              const progressColor = getProgressColor(progress);

              return (
                <ListItem
                  key={task.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'background.default',
                    borderRadius: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: daysLeft <= 1 ? 'error.200' : 'transparent',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={() => navigate(`/tasks?taskId=${task.id}`)}
                >
                  {/* Top row: title + priority + add to today button */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        flex: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.title}
                    </Typography>
                    <PriorityBadge priority={task.priority} size="small" />
                    {todaysPickedTaskIds.includes(task.id) && (
                      <Chip
                        label="Today"
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      disabled={
                        todaysPickedTaskIds.includes(task.id) ||
                        pickMutation.isPending ||
                        (atDailyPickLimit && !todaysPickedTaskIds.includes(task.id))
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        if (atDailyPickLimit && !todaysPickedTaskIds.includes(task.id)) {
                          setPickError(
                            carriedForwardCount > 0
                              ? `Daily pick limit reached (${maxDailyPicks} per day). ${carriedForwardCount} slot(s) used by tasks carried from yesterday.`
                              : `Daily pick limit reached (${maxDailyPicks} active tasks per day)`
                          );
                          return;
                        }
                        setConfirmDialog({ open: true, taskId: task.id, taskTitle: task.title });
                      }}
                      sx={{
                        fontSize: '0.75rem',
                        px: 1.5,
                        py: 0.5,
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                        '&.Mui-disabled': {
                          opacity: 0.4,
                        },
                      }}
                    >
                      {pickMutation.isPending ? (
                        <CircularProgress size={16} />
                      ) : todaysPickedTaskIds.includes(task.id) ? (
                        'Added'
                      ) : atDailyPickLimit ? (
                        'Limit reached'
                      ) : (
                        'Pick Today'
                      )}
                    </Button>
                  </Box>

                  {/* Middle row: date span + status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Tooltip title="Start → Due date range">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CalendarDays size={12} style={{ color: '#94a3b8' }} />
                        <Typography variant="caption" color="text.secondary">
                          {dayjs(task.startDate).format('MMM D')} → {dayjs(task.dueDate).format('MMM D')}
                        </Typography>
                      </Box>
                    </Tooltip>
                    <StatusBadge status={task.status} size="small" />
                    <Chip
                      size="small"
                      label={
                        daysLeft === 0
                          ? 'Due today'
                          : daysLeft < 0
                          ? `${Math.abs(daysLeft)} ${Math.abs(daysLeft) === 1 ? 'day' : 'days'} overdue`
                          : daysLeft === 1
                          ? '1 day left'
                          : `${daysLeft} days left`
                      }
                      color={daysLeft < 0 ? 'error' : daysLeft <= 1 ? 'error' : daysLeft <= 3 ? 'warning' : 'default'}
                      sx={{ height: 20, fontSize: '0.675rem', ml: 'auto' }}
                    />
                  </Box>

                  {/* Bottom row: progress bar + label */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        Time Window Progress
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {formatPercent(progress)} elapsed
                      </Typography>
                    </Box>
                    <Tooltip title={`${formatPercent(progress)} of time elapsed between start date (${dayjs(task.startDate).format('MMM D')}) and due date (${dayjs(task.dueDate).format('MMM D')})`}>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        color={progressColor}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: (theme) => alpha(theme.palette[progressColor].main, 0.12),
                        }}
                      />
                    </Tooltip>
                  </Box>
                </ListItem>
              );
            })}
            </List>
          </Box>
        )}
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, taskId: null, taskTitle: null })}
        aria-labelledby="pick-task-dialog-title"
        aria-describedby="pick-task-dialog-description"
      >
        <DialogTitle id="pick-task-dialog-title">
          Pick Task for Today?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="pick-task-dialog-description">
            Are you sure you want to add <strong>{confirmDialog.taskTitle}</strong> to today's tasks?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, taskId: null, taskTitle: null })} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              if (confirmDialog.taskId) {
                pickMutation.mutate(confirmDialog.taskId);
                setConfirmDialog({ open: false, taskId: null, taskTitle: null });
              }
            }} 
            color="primary"
            variant="contained"
            autoFocus
          >
            Yes, Pick It
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(pickError)}
        autoHideDuration={5000}
        onClose={() => setPickError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setPickError('')} sx={{ width: '100%' }}>
          {pickError}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default LiveTasksWidget;
